<template>
	<div class="cart-features">
		<div class="row">
			<div class="col-md-12 col-md-offset-1">
				<h5 class="cart-features-heading">Includes</h5>
				<div class="cart-features-container">
					<div class="first-row">
						<feature-icon v-for="feature in activeFeatures" :key="`active-${feature.id}`" :feature="feature" />
					</div>
					<div class="second-row">
						<feature-icon v-for="feature in inactiveFeatures" :key="`inactive-${feature.id}`" :feature="feature" />
					</div>
				</div>
				<div v-if="!allAccessoriesAreSelected" class="cart-accessories">
					<div class="row">
						<div class="col-md-12">
							<button class="btn btn-purple btn-outline cart-accessories-btn" @click="goToAccessories()">
								Add Accessories
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { heapAnalytics } from "@u/mixins.js";
import { mapGetters, mapState } from "vuex";
import CONSTANTS from "../../../../config/constants";
import FeatureIcon from "./feature-icon";

export default {
	name: "CartFeatures",
	components: {
		FeatureIcon
	},
	mixins: [
		heapAnalytics
	],
	props: {
		which: {
			type: String,
			required: true
		}
	},
	computed: {
		...mapState({
			cart: state => state.StarterPc.cart
		}),
		...mapGetters({
			trackStarterType: "StarterPc/getTrackType"
		}),
		allAccessoriesAreSelected() {
			return this.inactiveFeatures.filter(feature => feature.isActive).length == this.inactiveFeatures.length;
		},
		isBluetoothFeatureActive() {
			const motherboardActive = (this.cart.motherboard.bluetooth === "1");
			const extrasActive = this.cart.extras.some((extra) => (extra.bluetooth === "1"));

			return motherboardActive || extrasActive;
		},
		isRgbLightActive() {
			const activeRgbRoutes = ["streamer-pc", "creator-pc", "h1-mini-pc"];
			const currentRouteGroup = this.$route.meta.group;
			const isGroupAnActiveRoute = activeRgbRoutes.includes(currentRouteGroup);
			return isGroupAnActiveRoute;
		},
		activeFeatures() {
			return [
				{
					id: 1,
					image: {
						active: "/images/icons/cart-icons/lighting-active.svg",
						inactive: "/images/icons/cart-icons/lighting-inactive.svg"
					},
					text: "RGB Lighting",
					tooltip: {
						included: "This build is equipped with RGB components.",
						missing: "This build is not equipped with RGB components."
					},
					isActive: this.isRgbLightActive
				},
				{
					id: 2,
					image: {
						active: "/images/icons/cart-icons/signal-wifi-active.svg",
						inactive: "/images/icons/cart-icons/signal-wifi-inactive.svg"
					},
					text: "WiFi",
					tooltip: {
						included: "This build is WIFI ready.",
						missing: "This build does not have built-in WiFi capability."
					},
					isActive: true
				},
				{
					id: 3,
					image: {
						active: "/images/icons/cart-icons/bluetooth-active.svg",
						inactive: "/images/icons/cart-icons/bluetooth-inactive.svg"
					},
					text: "Bluetooth",
					tooltip: {
						included: "This build has Bluetooth capability for use with wireless Bluetooth products.",
						missing: "This build does not have Bluetooth capability for use with wireless Bluetooth products."
					},
					isActive: this.isBluetoothFeatureActive
				},
				{
					id: 4,
					image: {
						active: "/images/icons/cart-icons/usb-type-c-active.svg",
						inactive: "/images/icons/cart-icons/usb-type-c-inactive.svg"
					},
					text: "USB-C",
					tooltip: {
						included: "USB-C port on the front I/O panel of the case is functional.",
						missing: "USB-C port on the front I/O panel of the case is not functional."
					},
					isActive: this.cart.motherboard.external_usb_type_c === "1"
				},
				{
					id: 5,
					image: {
						active: "/images/icons/cart-icons/dude-active.svg"
					},
					text: "VR Ready",
					tooltip: {
						included: "This build meets the minimum recommended specs for VR functionality.",
						missing: "VR is not recommended for this configuration."
					},
					isActive: true
				}
			];
		},
		inactiveFeatures() {
			return [
				{
					id: 6,
					image: {
						active: "/images/icons/cart-icons/streaming-active.svg"
					},
					text: "Stream Ready",
					tooltip: {
						included: "This build will be capable of streaming most games in 720p with 60fps (internet speeds permitting). Games can only be streamed at 60+ FPS when running at 60+ FPS.",
						missing: "Streaming not recommended for this build."
					},
					isActive: true
				},
				{
					id: 7,
					image: {
						active: "/images/icons/cart-icons/display-active.svg",
						inactive: "/images/icons/cart-icons/display-inactive.svg"
					},
					text: "Monitor",
					tooltip: {
						included: "You have a monitor in your cart.",
						missing: "You do not have a monitor with your build."
					},
					isActive: !!this.cart.monitors[0].id
				},
				{
					id: 8,
					image: {
						active: "/images/icons/cart-icons/headset-active.svg",
						inactive: "/images/icons/cart-icons/headset-inactive.svg"
					},
					text: "Headset",
					tooltip: {
						included: "You have a headset in your cart.",
						missing: "You do not have a headset with your build."
					},
					isActive: !!this.cart.peripherals.some(item => item.peripherals_types_id == CONSTANTS.PERIPHERALS.HEADSET)
				},
				{
					id: 9,
					image: {
						active: "/images/icons/cart-icons/keyboard-active.svg",
						inactive: "/images/icons/cart-icons/keyboard-inactive.svg"
					},
					text: "Keyboard",
					tooltip: {
						included: "You have a keyboard in your cart.",
						missing: "You do not have a keyboard with your build."
					},
					isActive: !!this.cart.peripherals.some(item => item.peripherals_types_id == CONSTANTS.PERIPHERALS.KEYBOARD)
				},
				{
					id: 10,
					image: {
						active: "/images/icons/cart-icons/mouse-active.svg",
						inactive: "/images/icons/cart-icons/mouse-inactive.svg"
					},
					text: "Mouse",
					tooltip: {
						included: "You have a mouse in your cart.",
						missing: "You do not have a mouse with your build."
					},
					isActive: !!this.cart.peripherals.some(item => item.peripherals_types_id == CONSTANTS.PERIPHERALS.MOUSE)
				}
			];
		}
	},
	methods: {
		goToAccessories() {
			this.trackAll(`Client: Click ${this.trackStarterType} AddAccessories`, {
				page: "Cart Page"
			});
			this.$router.push({ name: `${this.which}-accessories` });
		}
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.cart-features {
	.col-md-offset-1 {
		@media (min-width: 992px) {
			margin-left: 13.333333%;
		}
	}
	.col-md-5 {
		padding-left: 0;
	}

	&-heading {
		margin: 0 0 25px;
		color: #141226;
		font-size: 16px;
		font-weight: bold;

		@media (min-width: 320px) and (max-width: 580px) {
			margin: 0 0 15px;
		}
	}

	&-container {
		display: flex;
		flex-direction: column;

		.first-row {
			margin-bottom: 13px;
		}

		.first-row,
		.second-row {
			display: flex;

			@media (min-width: 320px) and (max-width: 580px) {
				flex-wrap: wrap;
			}
		}
	}
}

.cart-accessories {
	.col-md-offset-1 {
		@media (min-width: 992px) {
			margin-left: 13.333333%;
		}
	}

	&-btn {
		border: 3px solid $purple;
		color: $purple;
		margin-bottom: 45px;
		margin-top: 13px;
		padding: 6px 33px;

		&:hover {
			background-color: $purple;
			border: 3px solid $purple;
			color: $white;
		}
	}
}
</style>
